import type { CorrectedTextItem, Bbox, Baseline, OcrSymbol } from "@/types/textFix";

type SmartSymbolReplacement = {
  bbox: Bbox;
  newChar: string;
  /** Позиция буквы в слове для выравнивания: первая — привязка по x1, последняя — по x0, в середине — по центру bbox */
  positionInWord?: "first" | "last" | "middle";
};

/** Эталонный символ строки для подбора шрифта и начертания (буква, которая не заменяется). */
type ReferenceSymbol = {
  bbox: Bbox;
  char: string;
};

/** По исправленному тексту (с пробелами) строит массив: для каждого индекса буквы в строке без пробелов — first/last/middle в слове. */
function getPositionInWordByIndex(
  corrected: string,
): ("first" | "last" | "middle")[] {
  const words = corrected.trim().split(/\s+/).filter(Boolean);
  const result: ("first" | "last" | "middle")[] = [];
  for (const word of words) {
    for (let j = 0; j < word.length; j++) {
      const isFirst = j === 0;
      const isLast = j === word.length - 1;
      result.push(
        isFirst && isLast
          ? "middle"
          : isFirst
            ? "first"
            : isLast
              ? "last"
              : "middle",
      );
    }
  }
  return result;
}

/** Популярные шрифты и начертания для перебора при подборе по эталонной букве. */
const FONT_CANDIDATES: { fontFamily: string; fontWeight: string }[] = (() => {
  const families = [
    "Arial",
    "Helvetica",
    "Times New Roman",
    "Georgia",
    "Verdana",
    "Tahoma",
    "Trebuchet MS",
    "Courier New",
    "system-ui",
    "sans-serif",
  ];
  const weights = ["400", "600", "700"];
  const list: { fontFamily: string; fontWeight: string }[] = [];
  for (const fontFamily of families) {
    for (const fontWeight of weights) {
      list.push({ fontFamily, fontWeight });
    }
  }
  return list;
})();

/**
 * Сравнивает два набора пикселей (grayscale) по MSE. Меньше — лучше совпадение.
 */
function imageDataMSE(
  dataA: Uint8ClampedArray,
  dataB: Uint8ClampedArray,
  width: number,
  height: number,
): number {
  let sumSq = 0;
  let n = 0;
  for (let j = 0; j < height; j++) {
    for (let i = 0; i < width; i++) {
      const idx = (j * width + i) * 4;
      const a =
        (dataA[idx] ?? 0) * 0.299 +
        (dataA[idx + 1] ?? 0) * 0.587 +
        (dataA[idx + 2] ?? 0) * 0.114;
      const b =
        (dataB[idx] ?? 0) * 0.299 +
        (dataB[idx + 1] ?? 0) * 0.587 +
        (dataB[idx + 2] ?? 0) * 0.114;
      sumSq += (a - b) ** 2;
      n++;
    }
  }
  return n === 0 ? Infinity : sumSq / n;
}

/**
 * По эталонной букве на канвасе подбирает шрифт и начертание:
 * рендерит ту же букву разными шрифтами/жирностью и выбирает вариант с минимальным MSE к исходному глифу.
 */
function detectFontAndWeightFromReference(
  ctx: CanvasRenderingContext2D,
  refBbox: Bbox,
  refChar: string,
  textColorRgba: { r: number; g: number; b: number; a: number },
  getBackgroundColor: (
    x0: number,
    y0: number,
    x1: number,
    y1: number,
  ) => { r: number; g: number; b: number; a: number },
  _baselineY?: number,
): { fontFamily: string; fontWeight: string } {
  const { x0, y0, x1, y1 } = refBbox;
  const w = Math.max(1, Math.round(x1 - x0));
  const h = Math.max(1, Math.round(y1 - y0));
  const canvas = ctx.canvas;
  const clampX = (v: number) => Math.max(0, Math.min(canvas.width, v));
  const clampY = (v: number) => Math.max(0, Math.min(canvas.height, v));
  const cx0 = clampX(x0);
  const cy0 = clampY(y0);
  const cw = Math.max(1, Math.min(w, canvas.width - cx0));
  const ch = Math.max(1, Math.min(h, canvas.height - cy0));

  const originalData = ctx.getImageData(cx0, cy0, cw, ch);
  const bg = getBackgroundColor(x0, y0, x1, y1);
  const textCss = `rgba(${textColorRgba.r}, ${textColorRgba.g}, ${textColorRgba.b}, ${textColorRgba.a / 255})`;
  const bgCss = `rgba(${bg.r}, ${bg.g}, ${bg.b}, ${bg.a / 255})`;

  const pickFontSize = (
    measureCtx: CanvasRenderingContext2D,
    fontFamily: string,
    fontWeight: string,
    maxHeight: number,
  ) => {
    let size = Math.max(8, maxHeight * 1.05);
    measureCtx.textBaseline = "alphabetic";
    for (let i = 0; i < 40; i++) {
      measureCtx.font = `${fontWeight} ${size}px ${fontFamily}`;
      const m = measureCtx.measureText(refChar);
      if (m.actualBoundingBoxAscent >= maxHeight) break;
      size += 1;
    }
    return size;
  };

  let bestMSE = Infinity;
  let best: { fontFamily: string; fontWeight: string } = {
    fontFamily: "Arial",
    fontWeight: "400",
  };

  const off = document.createElement("canvas");
  off.width = cw;
  off.height = ch;
  const offCtx = off.getContext("2d");
  if (!offCtx) return best;

  for (const { fontFamily, fontWeight } of FONT_CANDIDATES) {
    const fontSize = pickFontSize(offCtx, fontFamily, fontWeight, ch);
    offCtx.fillStyle = bgCss;
    offCtx.fillRect(0, 0, cw, ch);
    offCtx.fillStyle = textCss;
    offCtx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    offCtx.textBaseline = "alphabetic";
    offCtx.fillText(refChar, 0, ch);

    const renderedData = offCtx.getImageData(0, 0, cw, ch);
    const mse = imageDataMSE(originalData.data, renderedData.data, cw, ch);
    if (mse < bestMSE) {
      bestMSE = mse;
      best = { fontFamily, fontWeight };
    }
  }

  return best;
}

function applySmartSymbolReplacementsToCanvas(
  ctx: CanvasRenderingContext2D,
  baseImage: HTMLImageElement,
  lineBbox: Bbox,
  replacements: SmartSymbolReplacement[],
  baseline?: Baseline,
  referenceSymbol?: ReferenceSymbol,
) {
  const canvas = ctx.canvas;
  const width = canvas.width;
  const height = canvas.height;

  const { x0: lx0, y0: ly0, x1: lx1, y1: ly1 } = lineBbox;

  const clampRect = (x0: number, y0: number, x1: number, y1: number) => {
    const cx0 = Math.max(0, Math.min(width, x0));
    const cy0 = Math.max(0, Math.min(height, y0));
    const cx1 = Math.max(0, Math.min(width, x1));
    const cy1 = Math.max(0, Math.min(height, y1));
    return { cx0, cy0, cx1, cy1 };
  };

  /** Цвет фона как среднее по пикселям обводки буквы (кольцо на расстоянии pad px от bbox). */
  const getBackgroundColorFromRing = (
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    pad: number = 2,
  ) => {
    const outerX0 = x0 - pad;
    const outerY0 = y0 - pad;
    const outerX1 = x1 + pad;
    const outerY1 = y1 + pad;
    const { cx0, cy0, cx1, cy1 } = clampRect(
      outerX0,
      outerY0,
      outerX1,
      outerY1,
    );
    const w = Math.max(1, cx1 - cx0);
    const h = Math.max(1, cy1 - cy0);
    const imgData = ctx.getImageData(cx0, cy0, w, h);
    const data = imgData.data;

    const innerLeft = Math.max(0, x0 - cx0);
    const innerTop = Math.max(0, y0 - cy0);
    const innerRight = Math.min(w, x1 - cx0);
    const innerBottom = Math.min(h, y1 - cy0);

    let rSum = 0;
    let gSum = 0;
    let bSum = 0;
    let aSum = 0;
    let count = 0;

    for (let j = 0; j < h; j++) {
      for (let i = 0; i < w; i++) {
        const inInner =
          i >= innerLeft && i < innerRight && j >= innerTop && j < innerBottom;
        if (inInner) continue;

        const idx = (j * w + i) * 4;
        rSum += data[idx] ?? 0;
        gSum += data[idx + 1] ?? 0;
        bSum += data[idx + 2] ?? 0;
        aSum += data[idx + 3] ?? 0;
        count++;
      }
    }

    if (count === 0) {
      return { r: 248, g: 248, b: 248, a: 255 };
    }

    return {
      r: rSum / count,
      g: gSum / count,
      b: bSum / count,
      a: aSum / count,
    };
  };

  const getTextColorInRect = (
    x0: number,
    y0: number,
    x1: number,
    y1: number,
  ) => {
    const { cx0, cy0, cx1, cy1 } = clampRect(x0, y0, x1, y1);
    const w = Math.max(1, cx1 - cx0);
    const h = Math.max(1, cy1 - cy0);
    const imgData = ctx.getImageData(cx0, cy0, w, h);
    const data = imgData.data;

    type Pixel = { r: number; g: number; b: number; a: number; luma: number };
    const darkPixels: Pixel[] = [];
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] ?? 0;
      const g = data[i + 1] ?? 0;
      const b = data[i + 2] ?? 0;
      const a = data[i + 3] ?? 0;
      if (a <= 10) continue;
      const luma = 0.299 * r + 0.587 * g + 0.114 * b;
      darkPixels.push({ r, g, b, a, luma });
    }

    if (!darkPixels.length) {
      return { r: 0, g: 0, b: 0, a: 255 };
    }

    darkPixels.sort((p1, p2) => p1.luma - p2.luma);
    const sampleCount = Math.max(1, Math.floor(darkPixels.length * 0.05));

    let rSum = 0;
    let gSum = 0;
    let bSum = 0;
    let aSum = 0;
    for (let i = 0; i < sampleCount; i++) {
      const p = darkPixels[i]!;
      rSum += p.r;
      gSum += p.g;
      bSum += p.b;
      aSum += p.a;
    }

    return {
      r: rSum / sampleCount,
      g: gSum / sampleCount,
      b: bSum / sampleCount,
      a: aSum / sampleCount,
    };
  };

  const rgbaToCss = (c: { r: number; g: number; b: number; a: number }) =>
    `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a / 255})`;

  const textColor = getTextColorInRect(lx0, ly0, lx1, ly1);

  let fontFamily = "system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  let fontWeight = "400";
  if (referenceSymbol) {
    const detected = detectFontAndWeightFromReference(
      ctx,
      referenceSymbol.bbox,
      referenceSymbol.char,
      textColor,
      (x0, y0, x1, y1) => getBackgroundColorFromRing(x0, y0, x1, y1, 2),
      baseline?.y0,
    );

    fontFamily = detected.fontFamily;
    fontWeight = detected.fontWeight;
  }

  const pickFontSizeForWidth = (
    ch: string,
    maxHeight: number,
    startSize: number,
  ) => {
    ctx.textBaseline = "alphabetic";
    let size = startSize;
    for (let i = 0; i < 30; i++) {
      ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
      const m = ctx.measureText(ch);
      if (m.actualBoundingBoxAscent >= maxHeight) break;
      size += 1;
    }
    return size;
  };

  for (const rep of replacements) {
    const { bbox, newChar } = rep;
    const { x0, y0, x1, y1 } = bbox;
    const w = x1 - x0;
    const h = y1 - y0;
    if (w <= 0 || h <= 0 || !newChar) continue;

    const baseFontSize = Math.max(8, h * 1.05);
    const fontSize = pickFontSizeForWidth(newChar, h, baseFontSize);

    const localBg = getBackgroundColorFromRing(x0, y0, x1, y1, 2);
    ctx.fillStyle = rgbaToCss(localBg);
    const pad = 2;
    const fillX0 = Math.max(0, x0 - pad);
    const fillY0 = Math.max(0, y0 - pad);
    const fillX1 = Math.min(width, x1 + pad);
    const fillY1 = Math.min(height, y1 + pad);
    ctx.fillRect(fillX0, fillY0, fillX1 - fillX0, fillY1 - fillY0);

    ctx.fillStyle = rgbaToCss(textColor);
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textBaseline = "alphabetic";

    const baselineY = baseline != null ? baseline.y1 : y1;
    const measuredWidth = ctx.measureText(newChar).width;
    let targetX: number;
    switch (rep.positionInWord) {
      case "first":
        targetX = x1 - measuredWidth;
        break;
      case "last":
        targetX = x0;
        break;
      case "middle":
      default:
        targetX = x0 + (w - measuredWidth) / 2;
        break;
    }

    ctx.fillText(newChar, targetX, baselineY);
  }

  ctx.globalCompositeOperation = "destination-over";
  ctx.drawImage(baseImage, 0, 0, width, height);
  ctx.globalCompositeOperation = "source-over";
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Применяет исправленные текстовые элементы к изображению и возвращает data URL с перерисованными надписями. */
export async function applyTextFixesToImage(
  sourceImageDataUrl: string,
  correctedItems: CorrectedTextItem[],
): Promise<string> {
  const img = await loadImage(sourceImageDataUrl);

  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;

  const itemsWithBox = correctedItems.filter((i) => i.bbox);
  if (!itemsWithBox.length) {
    return sourceImageDataUrl;
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return sourceImageDataUrl;
  }

  ctx.drawImage(img, 0, 0, width, height);

  const normalize = (s: string) => s.replace(/ё/g, "е");

  for (const item of itemsWithBox) {
    const syms: OcrSymbol[] = item.symbols ?? [];
    const corrected = item.correctedText.trim();
    const originalFromSymbols = syms.map((s) => s.char).join("");
    const originalNorm = normalize(originalFromSymbols);
    const correctedNoSpaces = corrected.replace(/\s+/g, "");
    const correctedNorm = normalize(correctedNoSpaces);

    const canPerChar =
      syms.length > 0 &&
      originalNorm.length === correctedNorm.length &&
      originalFromSymbols.length === syms.length;

    if (!canPerChar && item.bbox) {
      const positionInWordByIndex = getPositionInWordByIndex(corrected);
      const replacementsForLine: SmartSymbolReplacement[] = [];
      for (let i = 0; i < syms.length && i < correctedNoSpaces.length; i++) {
        const sym = syms[i];
        const newChar = correctedNoSpaces[i];
        if (!sym || !newChar) continue;
        if (normalize(sym.char) === normalize(newChar)) continue;
        if (!sym.bbox) continue;
        const sourceSymbol = syms.find(
          (s) => normalize(s.char) === normalize(newChar),
        );
        if (sourceSymbol) {
          const { x0: tx0, y0: ty0, x1: tx1, y1: ty1 } = sym.bbox;
          const tw = tx1 - tx0;
          const th = ty1 - ty0;
          ctx.fillStyle = "rgba(255,255,255,0.95)";
          ctx.fillRect(tx0, ty0, tw, th);
          const { x0: sx0, y0: sy0, x1: sx1, y1: sy1 } = sourceSymbol.bbox;
          const sw = sx1 - sx0;
          const sh = sy1 - sy0;
          ctx.drawImage(img, sx0, sy0, sw, sh, tx0, ty0, tw, th);
        } else {
          replacementsForLine.push({
            bbox: {
              x0: sym.bbox.x0,
              y0: sym.bbox.y0,
              x1: sym.bbox.x1,
              y1: sym.bbox.y1,
            },
            newChar,
            positionInWord: positionInWordByIndex[i] ?? "middle",
          });
        }
      }

      if (replacementsForLine.length) {
        let referenceSymbol: ReferenceSymbol | undefined;
        for (
          let i = 0;
          i < syms.length && i < correctedNoSpaces.length;
          i++
        ) {
          const sym = syms[i];
          const newChar = correctedNoSpaces[i];
          if (
            sym?.bbox &&
            newChar &&
            normalize(sym.char) === normalize(newChar)
          ) {
            referenceSymbol = { bbox: sym.bbox, char: sym.char };
            break;
          }
        }
        applySmartSymbolReplacementsToCanvas(
          ctx,
          img,
          item.bbox,
          replacementsForLine,
          item.baseline,
          referenceSymbol,
        );
        continue;
      }

      const { x0, y0, x1, y1 } = item.bbox;
      const w = x1 - x0;
      const h = y1 - y0;
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fillRect(x0, y0, w, h);
      const fontSize = Math.max(10, Math.floor(h * 0.6));
      ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = "#000000";
      ctx.textBaseline = "middle";
      const paddingX = w * 0.08;
      const centerY = y0 + h / 2;
      let currentFontSize = fontSize;
      let measured = ctx.measureText(corrected);
      const maxWidth = w - paddingX * 2;
      while (measured.width > maxWidth && currentFontSize > 8) {
        currentFontSize -= 1;
        ctx.font = `bold ${currentFontSize}px Inter, system-ui, sans-serif`;
        measured = ctx.measureText(corrected);
      }
      ctx.fillText(corrected, x0 + paddingX, centerY);
      continue;
    }

    const replacements: {
      targetBbox: OcrSymbol["bbox"];
      newChar: string;
      index: number;
    }[] = [];
    for (let i = 0; i < syms.length; i++) {
      const sym = syms[i];
      if (!sym || originalNorm[i] === correctedNorm[i]) continue;
      replacements.push({
        targetBbox: sym.bbox,
        newChar: correctedNoSpaces[i] ?? "",
        index: i,
      });
    }

    const positionInWordByIndex = getPositionInWordByIndex(corrected);
    const smartReplacements: SmartSymbolReplacement[] = [];

    for (const { targetBbox, newChar, index } of replacements) {
      const { x0: tx0, y0: ty0, x1: tx1, y1: ty1 } = targetBbox;
      const tw = tx1 - tx0;
      const th = ty1 - ty0;

      const newNorm = normalize(newChar);
      const sourceSymbol = syms.find((s) => normalize(s.char) === newNorm);

      if (sourceSymbol) {
        ctx.fillStyle = "rgba(255,255,255,0.95)";
        ctx.fillRect(tx0, ty0, tw, th);
        const { x0: sx0, y0: sy0, x1: sx1, y1: sy1 } = sourceSymbol.bbox;
        const sw = sx1 - sx0;
        const sh = sy1 - sy0;
        ctx.drawImage(img, sx0, sy0, sw, sh, tx0, ty0, tw, th);
      } else {
        smartReplacements.push({
          bbox: { x0: tx0, y0: ty0, x1: tx1, y1: ty1 },
          newChar,
          positionInWord: positionInWordByIndex[index] ?? "middle",
        });
      }
    }

    if (smartReplacements.length > 0 && item.bbox) {
      let referenceSymbol: ReferenceSymbol | undefined;
      for (let i = 0; i < syms.length; i++) {
        if (originalNorm[i] !== correctedNorm[i]) continue;
        const sym = syms[i];
        if (sym?.bbox) {
          referenceSymbol = { bbox: sym.bbox, char: sym.char };
          break;
        }
      }
      applySmartSymbolReplacementsToCanvas(
        ctx,
        img,
        item.bbox,
        smartReplacements,
        item.baseline,
        referenceSymbol,
      );
    }
  }

  return canvas.toDataURL("image/png");
}

